import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import FastForwardRoundedIcon from '@mui/icons-material/FastForwardRounded';
import FastRewindRoundedIcon from '@mui/icons-material/FastRewindRounded';
import { Button, Grid, IconButton, Popover, Tooltip } from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { daDK } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';

// ---------- Helpers ----------
const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  const year = date.setLocale('da').toFormat('yyyy');
  return `${day} ${month} ${year}`;
};

// Snap BACK to a specific weekday (1..7, Mon..Sun)
const alignToWeekday = (date, anchorWeekday) => {
  const diff = (date.weekday - anchorWeekday + 7) % 7;
  return date.minus({ days: diff });
};

// Inclusive end-of-range from a start
const computeInclusiveEnd = (start, aggregate, globalDuration) => {
  if (aggregate === 'month') return start.endOf('month');
  const days = Math.max(0, Math.round(globalDuration) - 1);
  return start.plus({ days }).endOf('day');
};

// Previous (shifted) comparison start, weekday-aligned
const getComparisonStart = (date, aggregate, globalDuration, anchorWeekday) => {
  if (aggregate === 'month') return date.startOf('month').minus({ months: 1 });
  const days = Math.max(0, Math.round(globalDuration));
  const target = date.minus({ days });
  return alignToWeekday(target, anchorWeekday);
};

// Next (shifted) comparison start, weekday-aligned
const getNextComparisonStart = (
  date,
  aggregate,
  globalDuration,
  anchorWeekday,
) => {
  if (aggregate === 'month') return date.startOf('month').plus({ months: 1 });
  const days = Math.max(0, Math.round(globalDuration));
  const target = date.plus({ days });
  return alignToWeekday(target, anchorWeekday);
};

const SingleDateSelector = ({
  setComparisonRange,
  globalDuration,
  timeRange, // { start, end } in ms (UTC)
  isComparingSameNode,
  aggregate, // 'month' or day-based
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  // Base start-of-day from current timeRange (local zone)
  const baseStart = useMemo(
    () =>
      DateTime.fromMillis(timeRange.start)
        .setZone('Europe/Copenhagen')
        .startOf('day'),
    [timeRange.start],
  );

  // Anchor weekday is taken from the ORIGINAL timeRange.start
  const anchorWeekday = useMemo(
    () =>
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen').weekday,
    [timeRange.start],
  );

  const [selectedDate, setSelectedDate] = useState(baseStart);
  const [selectedDateEnd, setSelectedDateEnd] = useState(
    computeInclusiveEnd(baseStart, aggregate, globalDuration),
  );

  // Track whether the user has taken an action; prevents future auto-defaults
  const userTouchedRef = useRef(false);

  // Disable all dates that are not the anchored weekday
  const allowedWeekday = anchorWeekday; // 1..7 (Mon..Sun)
  const shouldDisableDate = (date) => date.weekday !== allowedWeekday;

  const canGoForward = () => {
    if (!isComparingSameNode) return false;

    const nextStart =
      aggregate === 'month'
        ? selectedDate.startOf('month').plus({ months: 1 })
        : getNextComparisonStart(
            selectedDate,
            aggregate,
            globalDuration,
            anchorWeekday,
          );

    const globalStart = baseStart; // the unshifted, current selection start
    return nextStart < globalStart;
  };

  const commitRange = (start, end) => {
    setSelectedDate(start);
    setSelectedDateEnd(end);

    const newRange = {
      start: start.toUTC().toMillis(),
      end: end.toUTC().toMillis(),
    };

    // Guard against redundant updates
    setComparisonRange((prev) => {
      if (!prev || prev.start !== newRange.start || prev.end !== newRange.end) {
        return newRange;
      }
      return prev;
    });
  };

  const handleDateChange = (date) => {
    if (!date) return;
    userTouchedRef.current = true;

    const calculatedStart = date.startOf('day'); // picker enforces weekday
    const calculatedEnd = computeInclusiveEnd(
      calculatedStart,
      aggregate,
      globalDuration,
    );

    commitRange(calculatedStart, calculatedEnd);
    setAnchorEl(null);
  };

  const handleGoBack = () => {
    userTouchedRef.current = true;

    const newStart = getComparisonStart(
      selectedDate,
      aggregate,
      globalDuration,
      anchorWeekday,
    );
    const newEnd = computeInclusiveEnd(newStart, aggregate, globalDuration);

    commitRange(newStart, newEnd);
  };

  const handleGoForward = () => {
    userTouchedRef.current = true;

    const newStart = getNextComparisonStart(
      selectedDate,
      aggregate,
      globalDuration,
      anchorWeekday,
    );
    const newEnd = computeInclusiveEnd(newStart, aggregate, globalDuration);

    commitRange(newStart, newEnd);
  };

  /**
   * Sync to external timeRange changes (or switching same-node compare on/off).
   * IMPORTANT: We intentionally do NOT depend on `aggregate` or `globalDuration`
   * here to avoid resetting the comparison range when `viewMode` changes.
   */
  useEffect(() => {
    // If user has interacted, don't auto-reset.
    if (userTouchedRef.current) return;

    const start = baseStart;

    let calculatedStart;
    let calculatedEnd;

    if (isComparingSameNode) {
      calculatedStart = getComparisonStart(
        start,
        aggregate, // ok to read current aggregate; effect doesn't re-run on it
        globalDuration,
        anchorWeekday,
      );
      calculatedEnd = computeInclusiveEnd(
        calculatedStart,
        aggregate,
        globalDuration,
      );
    } else {
      calculatedStart = start;
      calculatedEnd = DateTime.fromMillis(timeRange.end)
        .setZone('Europe/Copenhagen')
        .endOf('day'); // inclusive end when not shifting
    }

    // Initialize or re-sync if the range meaningfully changed due to timeRange flip
    commitRange(calculatedStart, calculatedEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseStart, timeRange.end, isComparingSameNode, anchorWeekday]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale="da"
      localeText={
        daDK.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Tooltip
              title={`Gå ${Math.round(globalDuration)} dage tilbage`}
              arrow
            >
              <IconButton
                onClick={handleGoBack}
                disabled={!isComparingSameNode}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  ':hover': { color: 'primary' },
                }}
              >
                <FastRewindRoundedIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          <Grid item>
            <Tooltip title={`Gå ${Math.round(globalDuration)} dage frem`} arrow>
              <IconButton
                onClick={handleGoForward}
                disabled={!canGoForward()}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  ':hover': { color: 'primary' },
                }}
              >
                <FastForwardRoundedIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          <Grid item xs>
            <Button
              startIcon={<CalendarTodayRoundedIcon />}
              size="large"
              variant="text"
              disabled={!isComparingSameNode}
              fullWidth
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                textTransform: 'none',
                color: 'text.primary',
              }}
            >
              {selectedDate
                ? `${formatDate(selectedDate)} - ${formatDate(selectedDateEnd)}`
                : 'Vælg startdato'}
            </Button>
          </Grid>
        </Grid>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <StaticDatePicker
            displayStaticWrapperAs="mobile"
            value={selectedDate}
            onChange={handleDateChange}
            maxDate={DateTime.now()}
            shouldDisableDate={shouldDisableDate}
          />
        </Popover>
      </>
    </LocalizationProvider>
  );
};

export default SingleDateSelector;
